import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { OutwriteApi } from '../services/OutwriteService';
import {
  extractPracticeLexis,
  groupTasksByGivenId,
  loadSessionState,
  resolveExplanation,
  saveSessionState,
  keyValueForToFind,
} from '../outwrite/learnerPracticeUtils';
import { LEARNER } from '../outwrite/learnerTokens';
import OutwriteLearnerChartCard from './OutwriteLearnerChartCard';
import OutwriteLearnerQuestion from './OutwriteLearnerQuestion';
import OutwriteLearnerWords from './OutwriteLearnerWords';
import OutwriteLearnerOverview from './OutwriteLearnerOverview';

/**
 * Practice session: Read the graph → Words → Write the overview.
 */
export default function OutwriteLearnerPractice() {
  const { givenId: givenIdParam } = useParams();
  const givenId = givenIdParam ? decodeURIComponent(givenIdParam) : '';
  const user = useSelector((state) => state.auth.user);

  const [listLoading, setListLoading] = useState(true);
  const [taskLoading, setTaskLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskIndex, setTaskIndex] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [preWriting, setPreWriting] = useState(null);
  const [overview, setOverview] = useState(null);

  const [phase, setPhase] = useState('questions'); // questions | words | overview
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const [overviewText, setOverviewText] = useState('');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setError(null);
    setPreWriting(null);
    setOverview(null);
    setHydrated(false);

    OutwriteApi.list()
      .then(async (list) => {
        if (cancelled) return;
        const groups = groupTasksByGivenId(list);
        const idx = groups.findIndex((g) => g.givenId === givenId);
        if (idx < 0) {
          setError('Task not found.');
          setListLoading(false);
          return;
        }
        const group = groups[idx];
        setTaskIndex(idx);
        setTaskCount(groups.length);
        setListLoading(false);
        setTaskLoading(true);

        const loads = [];
        if (group.preWritingId) {
          loads.push(
            OutwriteApi.get(group.preWritingId).then((t) => ({
              kind: 'pre',
              task: t,
            })),
          );
        }
        if (group.overviewId) {
          loads.push(
            OutwriteApi.get(group.overviewId).then((t) => ({
              kind: 'ov',
              task: t,
            })),
          );
        }
        const parts = await Promise.all(loads);
        if (cancelled) return;
        for (const p of parts) {
          if (p.kind === 'pre') setPreWriting(p.task);
          if (p.kind === 'ov') setOverview(p.task);
        }
        setTaskLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load practice task.');
          setListLoading(false);
          setTaskLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [givenId]);

  useEffect(() => {
    if (!givenId || listLoading || taskLoading || hydrated) return;
    const saved = loadSessionState(givenId);
    if (saved) {
      if (saved.phase) setPhase(saved.phase);
      if (typeof saved.questionIndex === 'number') {
        setQuestionIndex(saved.questionIndex);
      }
      if (saved.answers) setAnswers(saved.answers);
      if (saved.results) setResults(saved.results);
      if (typeof saved.overviewText === 'string') {
        setOverviewText(saved.overviewText);
      }
    }
    setHydrated(true);
  }, [givenId, listLoading, taskLoading, hydrated]);

  useEffect(() => {
    if (!givenId || !hydrated) return;
    saveSessionState(givenId, {
      phase,
      questionIndex,
      answers,
      results,
      overviewText,
    });
  }, [givenId, hydrated, phase, questionIndex, answers, results, overviewText]);

  const chartTask = preWriting || overview;
  const toFinds = useMemo(
    () => (Array.isArray(preWriting?.to_finds) ? preWriting.to_finds : []),
    [preWriting],
  );
  const currentToFind = toFinds[questionIndex] || null;
  const lexis = useMemo(() => extractPracticeLexis(overview), [overview]);

  const seriesWordsForCurrent = useMemo(() => {
    if (!currentToFind || !lexis.series.length) return [];
    const labels = Object.keys(chartTask?.given?.data?.series || {});
    if (labels.length === 1) {
      return lexis.series.find((s) => s.name === labels[0])?.words || [];
    }
    return [];
  }, [currentToFind, lexis, chartTask]);

  const answerSummaries = useMemo(() => {
    return toFinds.map((tf) => {
      const r = results[tf.id];
      const a = answers[tf.id];
      const keyVal = keyValueForToFind(preWriting, tf);
      const expl = resolveExplanation(
        tf,
        a?.type === 'option' ? a.value : null,
        keyVal,
      );
      if (expl) return expl;
      if (a?.type === 'option' && a.value) return a.value;
      if (a?.type === 'segmented' && a.selections) {
        return Object.entries(a.selections)
          .map(([k, v]) => `${k}: ${v}`)
          .join('; ');
      }
      if (r?.correct) return `Answered: ${tf.id}`;
      return null;
    }).filter(Boolean);
  }, [toFinds, results, answers, preWriting]);

  const userLabel =
    user?.login ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'learner';

  const onChangeAnswer = useCallback(
    (next) => {
      if (!currentToFind) return;
      setAnswers((prev) => ({ ...prev, [currentToFind.id]: next }));
      setResults((prev) => {
        const copy = { ...prev };
        delete copy[currentToFind.id];
        return copy;
      });
    },
    [currentToFind],
  );

  const onCheck = useCallback(
    (outcome) => {
      if (!currentToFind) return;
      setResults((prev) => ({ ...prev, [currentToFind.id]: outcome }));
    },
    [currentToFind],
  );

  const onNext = useCallback(() => {
    if (questionIndex >= toFinds.length - 1) {
      setPhase('words');
      return;
    }
    setQuestionIndex((i) => i + 1);
  }, [questionIndex, toFinds.length]);

  const goToQuestion = (i) => {
    if (i < 0 || i >= toFinds.length) return;
    setPhase('questions');
    setQuestionIndex(i);
  };

  if (listLoading || taskLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          justifyContent: 'center',
          bgcolor: LEARNER.surfaceContainerLow,
          minHeight: 320,
        }}
      >
        <CircularProgress sx={{ color: LEARNER.primary }} />
      </Box>
    );
  }

  if (error || !chartTask) {
    return (
      <Box sx={{ p: 3, bgcolor: LEARNER.surfaceContainerLow }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {error || 'Task unavailable.'}
        </Typography>
        <Button component={RouterLink} to="/outwrite/learn" sx={{ textTransform: 'none' }}>
          Back to practice list
        </Button>
      </Box>
    );
  }

  const stepReadActive = phase === 'questions' || phase === 'words';
  const stepWriteActive = phase === 'overview';

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: LEARNER.surfaceContainerLow,
        color: LEARNER.onSurface,
        fontFamily: 'Roboto, Arial, sans-serif',
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 3,
          minHeight: 64,
          bgcolor: LEARNER.surface,
          borderBottom: `1px solid ${LEARNER.outlineVariantSoft}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            component={RouterLink}
            to="/outwrite/learn"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              bgcolor: LEARNER.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              color: LEARNER.onPrimary,
              fontSize: 14,
              fontWeight: 700,
            }}
            aria-label="Practice home"
          >
            P
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 500 }}>Plotline</Typography>
        </Stack>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: LEARNER.onSurfaceVariant }}>
          Task {taskIndex + 1} of {taskCount || 1}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              fontSize: 14,
              color: LEARNER.onSurfaceVariant,
              px: 1.5,
              py: 0.75,
              border: `1px solid ${LEARNER.outlineVariant}`,
              borderRadius: '8px',
            }}
          >
            {userLabel}
          </Box>
          <IconButton aria-label="Help" size="small" sx={{ color: LEARNER.onSurfaceVariant }}>
            <HelpOutlineIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        component="nav"
        aria-label="Steps"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          bgcolor: LEARNER.surface,
          borderBottom: `1px solid ${LEARNER.outlineVariantSoft}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ cursor: 'pointer' }}
          onClick={() => setPhase(toFinds.length ? 'questions' : 'overview')}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '12px',
              bgcolor: stepReadActive ? LEARNER.primary : 'transparent',
              border: stepReadActive ? 0 : `1px solid ${LEARNER.outline}`,
              color: stepReadActive ? LEARNER.onPrimary : LEARNER.onSurfaceVariant,
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            1
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: stepReadActive ? 500 : 400,
              color: stepReadActive ? LEARNER.onSurface : LEARNER.onSurfaceVariant,
            }}
          >
            Read the graph
          </Typography>
        </Stack>
        <Box sx={{ width: 48, height: 1, bgcolor: LEARNER.outlineVariant }} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ cursor: 'pointer' }}
          onClick={() => setPhase('overview')}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '12px',
              bgcolor: stepWriteActive ? LEARNER.primary : 'transparent',
              border: stepWriteActive ? 0 : `1px solid ${LEARNER.outline}`,
              color: stepWriteActive ? LEARNER.onPrimary : LEARNER.onSurfaceVariant,
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            2
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: stepWriteActive ? 500 : 400,
              color: stepWriteActive ? LEARNER.onSurface : LEARNER.onSurfaceVariant,
            }}
          >
            Write the overview
          </Typography>
        </Stack>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'stretch',
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          boxSizing: 'border-box',
          p: 3,
          columnGap: 3,
          rowGap: 3,
        }}
      >
        <OutwriteLearnerChartCard task={chartTask} />

        {phase === 'questions' && currentToFind ? (
          <OutwriteLearnerQuestion
            task={preWriting}
            toFind={currentToFind}
            questionIndex={questionIndex}
            questionCount={toFinds.length}
            answer={answers[currentToFind.id]}
            result={results[currentToFind.id]}
            seriesWords={
              results[currentToFind.id] ? seriesWordsForCurrent : []
            }
            onChangeAnswer={onChangeAnswer}
            onCheck={onCheck}
            onNext={onNext}
          />
        ) : null}

        {phase === 'questions' && !currentToFind ? (
          <Box sx={{ flex: '1 1 480px' }}>
            <Typography sx={{ mb: 2 }}>
              No pre-writing questions for this graph. Continue to writing.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setPhase('overview')}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                bgcolor: LEARNER.primary,
              }}
            >
              Start writing
            </Button>
          </Box>
        ) : null}

        {phase === 'words' ? (
          <OutwriteLearnerWords
            lexis={lexis}
            answerSummaries={answerSummaries}
            onStartWriting={() => setPhase('overview')}
          />
        ) : null}

        {phase === 'overview' ? (
          <OutwriteLearnerOverview
            overviewTask={overview}
            text={overviewText}
            onTextChange={setOverviewText}
            lexis={lexis}
            onBackToQuestions={() => {
              setPhase(toFinds.length ? 'questions' : 'words');
            }}
          />
        ) : null}
      </Box>

      {phase !== 'overview' ? (
        <Box
          component="footer"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 3,
            py: 1.5,
            bgcolor: LEARNER.surface,
            borderTop: `1px solid ${LEARNER.outlineVariantSoft}`,
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {toFinds.map((tf, i) => {
              const done = Boolean(results[tf.id]);
              const current = phase === 'questions' && i === questionIndex;
              return (
                <Button
                  key={tf.id}
                  onClick={() => goToQuestion(i)}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    borderRadius: '18px',
                    textTransform: 'none',
                    fontWeight: 500,
                    bgcolor: current
                      ? LEARNER.primary
                      : done
                        ? LEARNER.secondaryContainer
                        : 'transparent',
                    color: current
                      ? LEARNER.onPrimary
                      : done
                        ? LEARNER.onSecondaryContainer
                        : LEARNER.onSurfaceVariant,
                    border: current || done ? 0 : `1px solid ${LEARNER.outlineVariant}`,
                    '&:hover': {
                      bgcolor: current
                        ? LEARNER.primary
                        : done
                          ? LEARNER.secondaryContainer
                          : LEARNER.surfaceContainerLow,
                    },
                  }}
                >
                  {i + 1}
                </Button>
              );
            })}
          </Stack>
          <Typography sx={{ fontSize: 12, color: LEARNER.onSurfaceVariant }}>
            Answers are saved automatically
          </Typography>
        </Box>
      ) : (
        <Box
          component="footer"
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: LEARNER.surface,
            borderTop: `1px solid ${LEARNER.outlineVariantSoft}`,
            textAlign: 'right',
          }}
        >
          <Typography sx={{ fontSize: 12, color: LEARNER.onSurfaceVariant }}>
            Your text is saved automatically
          </Typography>
        </Box>
      )}
    </Box>
  );
}
